// Brief B4 §1 — Admin beta launch dashboard.
//
// One-page metrics overview for OpenSapiens admins. Renders 5 blocks:
//   1) Acquisition (7-day bar chart + total users + 70% completeness)
//   2) Sessions (created, booked, done, conversion)
//   3) Engagement (DM, MaIA chats, posts, reviews avg)
//   4) Top 5 experts (with link to MaIA diagnostic)
//   5) Latest 10 signups
// Plus 2 action buttons: "Envoyer rappels avis" + "Cleanup données de test".
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Calendar, MessageSquare, Sparkles, Star, FileText,
  ShieldAlert, Loader2, Mail, Trash2, Activity, TrendingUp, BadgeCheck, BarChart3,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Card = ({ icon: Icon, label, value, sub, testid, tint = 'purple' }) => {
  const tints = {
    purple: 'from-purple-500/10 to-pink-500/10 border-purple-200 text-purple-600',
    cyan: 'from-cyan-500/10 to-teal-500/10 border-cyan-200 text-cyan-600',
    emerald: 'from-emerald-500/10 to-green-500/10 border-emerald-200 text-emerald-600',
    amber: 'from-amber-500/10 to-yellow-500/10 border-amber-200 text-amber-600',
    indigo: 'from-indigo-500/10 to-purple-500/10 border-indigo-200 text-indigo-600',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-gradient-to-br ${tints[tint]} dark:bg-gray-800/30 dark:border-gray-700 p-4 transition-shadow hover:shadow-md`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wide font-semibold opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white" data-testid={`${testid}-value`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
};

const ExpertRow = ({ row, metricLabel, onDiag }) => (
  <li className="flex items-center justify-between gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50">
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={row.avatar} />
        <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white text-xs">
          {(row.display_name || '?').split(' ').map(n => n[0]).join('').substring(0, 2)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.display_name}</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{row.email || row.user_id}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700">
        {row.count} {metricLabel}
      </span>
      <button
        type="button"
        onClick={() => onDiag(row.user_id)}
        className="text-[11px] text-cyan-700 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-100 underline-offset-2 hover:underline"
        data-testid={`admin-top-expert-diag-${row.user_id}`}
      >
        Diagnostic
      </button>
    </div>
  </li>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  // Brief Audit Beta Feb 2026 — DB-backed FORCE_FREE_SESSIONS toggle
  const [forceFree, setForceFree] = useState(null);
  const [togglingForceFree, setTogglingForceFree] = useState(false);
  // Brief Jul 3 2026 — Bouton "Gérer mon compte Stripe" pour l'admin connecté.
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeDashboardBusy, setStripeDashboardBusy] = useState(false);
  // Brief Sep 2026 — exports CSV (période sélectionnée)
  const [exportDays, setExportDays] = useState('30');

  useEffect(() => {
    const run = async () => {
      try {
        const { data: who } = await axios.get(`${API}/admin/me/is-admin`, { withCredentials: true });
        if (!who.is_admin) {
          setAuthorized(false);
          setTimeout(() => navigate('/app/home', { replace: true }), 1200);
          return;
        }
        setAuthorized(true);
        const { data: dash } = await axios.get(`${API}/admin/beta-dashboard`, { withCredentials: true });
        setData(dash);
        // Brief Audit Beta Feb 2026 — fetch the toggle state
        try {
          const { data: toggle } = await axios.get(`${API}/admin/settings/force-free-sessions`, { withCredentials: true });
          setForceFree(!!toggle.force_free_sessions);
        } catch { /* silent */ }
        // Fetch Stripe Connect status for the current admin (also expert).
        try {
          const { data: st } = await axios.get(`${API}/stripe/connect/status`, { withCredentials: true });
          setStripeConnected(!!st?.charges_enabled);
        } catch { /* silent */ }
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        toast.error(err?.response?.data?.detail || 'Erreur de chargement du dashboard');
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/cron/review-reminders`, {}, { withCredentials: true });
      toast.success(`${r.sent} rappel${r.sent > 1 ? 's' : ''} envoyé${r.sent > 1 ? 's' : ''} (${r.candidates_scanned} bookings scannés)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi des rappels');
    } finally {
      setSendingReminders(false);
    }
  };

  // Brief Jul 7 2026 §2 — rappels de session (24h / 1h avant).
  const [sendingSessionReminders, setSendingSessionReminders] = useState(false);
  // Sep 2026 — purge des comptes de test devenue run-once au boot :
  // relance manuelle depuis le dashboard (comptes protégés jamais touchés).
  const [runningCleanup, setRunningCleanup] = useState(false);
  const handleRunCleanup = async () => {
    if (!window.confirm('Lancer la purge des comptes de test ? (suppression définitive des comptes jetables + contenu orphelin — les comptes protégés ne sont jamais touchés)')) return;
    setRunningCleanup(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/cron/cleanup-test-accounts`, {}, { withCredentials: true });
      toast.success(`Purge terminée — ${r.deleted_users || 0} compte(s) supprimé(s), ${r.masked || 0} masqué(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setRunningCleanup(false);
    }
  };

  const handleSendSessionReminders = async () => {
    setSendingSessionReminders(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/cron/session-reminders`, {}, { withCredentials: true });
      toast.success(`${r.sent} rappel${r.sent > 1 ? 's' : ''} envoyé${r.sent > 1 ? 's' : ''} (24h: ${r.sent_24h} · 1h: ${r.sent_1h} · experts: ${r.sent_expert})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi des rappels de session');
    } finally {
      setSendingSessionReminders(false);
    }
  };

  // Brief pré-beta (Task 2) — test manuel de l'email hebdo de prospection.
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const handleSendWeeklyProspection = async () => {
    setSendingWeekly(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/cron/weekly-prospection`, {}, { withCredentials: true });
      toast.success(`Email hebdo de test envoyé à ton adresse (${r.sent} envoi${r.sent > 1 ? 's' : ''}) — vérifie ta boîte mail`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email hebdo');
    } finally {
      setSendingWeekly(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    if (stripeDashboardBusy) return;
    setStripeDashboardBusy(true);
    try {
      const { data } = await axios.post(`${API}/stripe/connect/dashboard`, {}, { withCredentials: true });
      if (data?.url) window.open(data.url, '_blank');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible d\'ouvrir le dashboard Stripe.');
    } finally {
      setStripeDashboardBusy(false);
    }
  };

  const handleToggleForceFree = async () => {
    if (forceFree == null || togglingForceFree) return;
    const next = !forceFree;
    setTogglingForceFree(true);
    try {
      await axios.post(
        `${API}/admin/settings/force-free-sessions`,
        { force_free_sessions: next },
        { withCredentials: true },
      );
      setForceFree(next);
      toast.success(next ? 'Sessions gratuites forcées (beta)' : 'Paiements Stripe activés');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur toggle');
    } finally {
      setTogglingForceFree(false);
    }
  };

  const handleCleanup = async () => {    setCleaningUp(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/cleanup/test-data`, {}, { withCredentials: true });
      const total = Object.values(r).reduce((acc, v) => (typeof v === 'number' ? acc + v : acc), 0);
      toast.success(`${total} item${total > 1 ? 's' : ''} supprimé${total > 1 ? 's' : ''}`);
      setCleanupConfirmOpen(false);
      // Reload dashboard data
      const { data: dash } = await axios.get(`${API}/admin/beta-dashboard`, { withCredentials: true });
      setData(dash);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du cleanup');
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="admin-dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      </div>
    );
  }

  if (authorized === false) {
    return (
      <div className="max-w-md mx-auto py-16 text-center" data-testid="admin-dashboard-forbidden">
        <ShieldAlert className="h-10 w-10 mx-auto text-red-500 mb-3" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Accès réservé</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Cette page est réservée aux administrateurs.</p>
        <p className="text-xs text-gray-400 mt-3">Redirection en cours…</p>
      </div>
    );
  }

  if (!data) return null;
  const { acquisition, sessions, engagement, top_experts, latest_users } = data;

  return (
    <div className="max-w-6xl mx-auto py-6 px-3 sm:px-6 space-y-8" data-testid="admin-dashboard-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-purple-500" />
            Dashboard admin · Beta
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Vue d'ensemble du lancement OpenSapiens — {new Date(data.generated_at).toLocaleString('fr-FR')}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {stripeConnected && (
            <button
              type="button"
              onClick={handleOpenStripeDashboard}
              disabled={stripeDashboardBusy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 disabled:opacity-60"
              data-testid="admin-stripe-dashboard-btn"
            >
              {stripeDashboardBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Gérer mon compte Stripe →
            </button>
          )}
          {forceFree != null && (
            <button
              type="button"
              onClick={handleToggleForceFree}
              disabled={togglingForceFree}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 ${
                forceFree
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-100'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100'
              }`}
              data-testid="admin-toggle-force-free"
              title={forceFree
                ? 'Toutes les sessions sont gratuites (soft-launch). Cliquer pour activer les paiements Stripe.'
                : 'Les paiements Stripe sont actifs. Cliquer pour repasser en mode gratuit.'}
            >
              {togglingForceFree ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <span className={`inline-block w-2 h-2 rounded-full ${forceFree ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              )}
              {forceFree ? 'Sessions gratuites (beta)' : 'Paiements Stripe actifs'}
            </button>
          )}
          <button
            type="button"
            onClick={handleSendReminders}
            disabled={sendingReminders}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-200 disabled:opacity-60"
            data-testid="admin-send-reminders-btn"
          >
            {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Envoyer rappels avis
          </button>
          {/* Brief Jul 27 2026 §2 — indicateur mode Stripe (préfixe de la clé) */}
          <span
            data-testid="admin-stripe-mode-badge"
            title="Détecté depuis le préfixe de la clé STRIPE_SECRET_KEY (sk_test_ / sk_live_)"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold ${
              data?.stripe_mode === 'live'
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            }`}
          >
            Mode Stripe : {data?.stripe_mode === 'live' ? 'LIVE' : 'TEST'}
          </span>
          <button
            type="button"
            onClick={handleSendSessionReminders}
            disabled={sendingSessionReminders}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 disabled:opacity-60"
            data-testid="admin-send-session-reminders-btn"
          >
            {sendingSessionReminders ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Envoyer rappels sessions
          </button>
          <button
            type="button"
            onClick={handleRunCleanup}
            disabled={runningCleanup}
            title="Purge manuelle des comptes de test (run-once au boot depuis sept. 2026)"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 disabled:opacity-60"
            data-testid="admin-cleanup-test-accounts-btn"
          >
            {runningCleanup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Purger comptes de test
          </button>
          <button
            type="button"
            onClick={handleSendWeeklyProspection}
            disabled={sendingWeekly}
            title="Envoie l'email « Ta semaine de prospection » à ton adresse uniquement (test)"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 disabled:opacity-60"
            data-testid="admin-weekly-prospection-btn"
          >
            {sendingWeekly ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Tester l'email hebdo
          </button>
          <button
            type="button"
            onClick={() => setCleanupConfirmOpen(true)}
            disabled={cleaningUp}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 disabled:opacity-60"
            data-testid="admin-cleanup-btn"
          >
            <Trash2 className="h-4 w-4" /> Cleanup données de test
          </button>
        </div>
      </header>

      {/* Bloc 1 — Acquisition */}
      <section data-testid="admin-block-acquisition">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Acquisition</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card icon={Users} label="Inscrits totaux" value={acquisition.total_users} testid="admin-total-users" tint="purple" />
          <Card icon={TrendingUp} label="Nouveaux 7j" value={acquisition.new_users_7d} testid="admin-new-users-7d" tint="cyan" />
          <Card
            icon={BadgeCheck}
            label="Profils ≥70%"
            value={`${acquisition.profile_completed_70} (${acquisition.profile_completed_70_pct}%)`}
            testid="admin-profile-completed"
            tint="emerald"
          />
        </div>
        <div className="mt-4 p-4 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Inscrits par jour (7 derniers)</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={acquisition.daily_signups} margin={{ top: 5, right: 5, bottom: 0, left: 0 }} data-testid="admin-signups-chart">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(127,127,127,0.15)" />
              <XAxis dataKey="label" stroke="#888" fontSize={11} />
              <YAxis allowDecimals={false} stroke="#888" fontSize={11} />
              <Tooltip cursor={{ fill: 'rgba(127,127,127,0.05)' }} />
              <Bar dataKey="count" fill="#7F77DD" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Bloc 2 — Sessions */}
      <section data-testid="admin-block-sessions">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Sessions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card icon={Calendar} label="Sessions créées" value={sessions.sessions_total} sub={`+${sessions.sessions_7d} sur 7j`} testid="admin-sessions-total" tint="indigo" />
          <Card icon={BadgeCheck} label="Réservations" value={sessions.bookings_total} sub={`+${sessions.bookings_7d} sur 7j`} testid="admin-bookings-total" tint="cyan" />
          <Card icon={Activity} label="Réalisées" value={sessions.sessions_done} testid="admin-sessions-done" tint="emerald" />
          <Card icon={TrendingUp} label="Conversion" value={`${sessions.conversion_rate}%`} sub="réservée → réalisée" testid="admin-conversion-rate" tint="purple" />
        </div>
      </section>

      {/* Bloc 3 — Engagement */}
      <section data-testid="admin-block-engagement">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Engagement</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card icon={MessageSquare} label="DMs envoyés" value={engagement.dm_messages_total} testid="admin-dm-total" tint="cyan" />
          <Card icon={Sparkles} label="Interactions MaIA" value={engagement.maia_interactions} testid="admin-maia-interactions" tint="purple" />
          <Card icon={FileText} label="Posts publiés" value={engagement.posts_total} testid="admin-posts-total" tint="indigo" />
          <Card icon={Star} label="Avis laissés" value={engagement.reviews_total} sub={engagement.avg_rating != null ? `Moyenne ${engagement.avg_rating}/5` : '—'} testid="admin-reviews-total" tint="amber" />
        </div>
      </section>

      {/* Bloc 4 — Top experts */}
      <section data-testid="admin-block-top-experts">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Top 5 experts</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {[
            { key: 'by_sessions_created', title: 'par sessions créées', metricLabel: 'sessions' },
            { key: 'by_bookings', title: 'par réservations reçues', metricLabel: 'bookings' },
            { key: 'by_reviews', title: 'par avis reçus', metricLabel: 'avis' },
          ].map(b => (
            <div key={b.key} className="p-4 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">{b.title}</p>
              <ul className="space-y-0.5">
                {(top_experts[b.key] || []).slice(0, 5).map(row => (
                  <ExpertRow key={`${b.key}-${row.user_id}`} row={row} metricLabel={b.metricLabel} onDiag={(uid) => navigate(`/app/admin/maia/${uid}`)} />
                ))}
                {(top_experts[b.key] || []).length === 0 && (
                  <li className="text-xs text-gray-400 py-2 px-2">Aucun expert pour cette métrique.</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Bloc 5 — Derniers inscrits */}
      <section data-testid="admin-block-latest-users">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">10 derniers inscrits</h2>
        <div className="rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100 dark:border-gray-700">
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2 hidden sm:table-cell">Email</th>
                <th className="px-3 py-2 hidden md:table-cell">Inscrit le</th>
                <th className="px-3 py-2 text-right">Profil</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {latest_users.map(u => (
                <tr key={u.user_id} className="border-b border-gray-50 dark:border-gray-700/40 last:border-0">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarImage src={u.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white text-[10px]">
                          {(u.display_name || '?').split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{u.display_name || '—'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{u.email}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs text-gray-500 dark:text-gray-400">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        u.profile_completeness >= 70
                          ? 'bg-emerald-100 text-emerald-700'
                          : u.profile_completeness >= 40
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {u.profile_completeness}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => navigate(`/app/admin/maia/${u.user_id}`)}
                      className="text-[11px] text-cyan-700 hover:underline"
                      data-testid={`admin-latest-diag-${u.user_id}`}
                    >
                      MaIA
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Brief Sep 2026 — Exports CSV (suivi beta). RGPD : pas d'emails dans les fichiers. */}
      <section data-testid="admin-block-exports">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Exports CSV</h2>
        <div className="rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700 p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Période :</span>
            {[['30', '30 jours'], ['90', '90 jours'], ['all', 'Tout']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setExportDays(val)}
                data-testid={`admin-export-period-${val}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  exportDays === val
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-purple-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[['users', 'Utilisateurs'], ['bookings', 'Réservations'], ['sessions', 'Sessions'], ['transactions', 'Transactions']].map(([kind, label]) => (
              <a
                key={kind}
                href={`${API}/admin/export/${kind}.csv?days=${exportDays}`}
                data-testid={`admin-export-${kind}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
              >
                Exporter {label}
              </a>
            ))}
          </div>
          <p className="text-[11px] text-gray-400">
            Conforme RGPD : les fichiers n'incluent pas d'adresses email — identification par user_id + nom d'affichage.
          </p>
        </div>
      </section>

      {/* Cleanup confirm dialog */}
      {cleanupConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-cleanup-confirm">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setCleanupConfirmOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cleanup données de test ?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5">
              Cette action supprime tous les posts/events/users marqués <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-xs">TEST_</code> ainsi que les comptes <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-xs">test_e2e</code>. Les comptes <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-xs">steve.stoppa*</code> sont préservés.
              <br />Cette action est <strong>irréversible</strong>. Continuer ?
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setCleanupConfirmOpen(false)}
                className="px-4 py-2 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                data-testid="admin-cleanup-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleCleanup}
                disabled={cleaningUp}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-60"
                data-testid="admin-cleanup-confirm-btn"
              >
                {cleaningUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Confirmer le cleanup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
